/**
 * .xlsx writer — single-sheet (`build_xlsx`, the legacy ledger-web surface) and
 * multi-sheet (`buildWorkbookXlsx`, for migration templates). Zero-dependency,
 * STORE-method ZIP. Generalised from apps/ledger-web/src/lib/export_xlsx.ts.
 */

import { buildZip, toUtf8 } from './zip_write.js';
import { isoDateToExcelSerial } from '../serial_date.js';

/** Cell value accepted by the writer: a display string or a numeric value. */
export type XlsxCell = string | number;

function xmlEscape(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** 0-based column index → Excel letter (0→A, 25→Z, 26→AA, …). */
function colLetter(idx: number): string {
  let name = '';
  let n = idx + 1;
  while (n > 0) {
    const m = (n - 1) % 26;
    name = String.fromCharCode(65 + m) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

// ---------------------------------------------------------------------------
// Data validations (dropdowns + date pickers)
// ---------------------------------------------------------------------------

/**
 * A dropdown restricting a column's data rows to a fixed list. Applied to rows
 * 2..MAX_VALIDATION_ROW so it covers any row the user fills in.
 */
export interface ListColumnValidation {
  col_index: number;
  type?: 'list';
  options: string[];
}

/** Constrains a column's data rows to a real date on/after `min`. */
export interface DateColumnValidation {
  col_index: number;
  type: 'date';
  min?: string; // ISO YYYY-MM-DD
}

export type ColumnValidation = ListColumnValidation | DateColumnValidation;

const MAX_VALIDATION_ROW = 1000;

/**
 * Excel's inline list `formula1` is a literal `"a,b,c"` string with a ~255
 * character ceiling, and a comma inside an option would break the delimiter.
 * Skip emitting the validation rather than writing a corrupt one.
 */
function buildListValidationXml(colIndex: number, options: string[]): string {
  if (options.some((o) => o.includes(','))) return '';
  const formula = `"${options.join(',')}"`;
  if (formula.length > 255) return '';
  const col = colLetter(colIndex);
  const sqref = `${col}2:${col}${MAX_VALIDATION_ROW}`;
  return (
    `<dataValidation type="list" allowBlank="1" showErrorMessage="1" sqref="${sqref}">` +
    `<formula1>${xmlEscape(formula)}</formula1>` +
    '</dataValidation>'
  );
}

function buildDateValidationXml(colIndex: number, min: string): string {
  const col = colLetter(colIndex);
  const sqref = `${col}2:${col}${MAX_VALIDATION_ROW}`;
  const minSerial = isoDateToExcelSerial(min) ?? 0;
  return (
    `<dataValidation type="date" operator="greaterThanOrEqual" allowBlank="1" showErrorMessage="1" ` +
    `errorTitle="Invalid date" error="Enter a real date (e.g. 15/07/2026)." sqref="${sqref}">` +
    `<formula1>${minSerial}</formula1>` +
    '</dataValidation>'
  );
}

function buildDataValidationXml(v: ColumnValidation): string {
  if (v.type === 'date') {
    return buildDateValidationXml(v.col_index, v.min ?? '2000-01-01');
  }
  return buildListValidationXml(v.col_index, v.options);
}

function buildDataValidationsBlock(validations: ColumnValidation[]): string {
  const xmls = validations
    .map((v) => buildDataValidationXml(v))
    .filter((xml) => xml.length > 0);
  return xmls.length > 0
    ? `<dataValidations count="${xmls.length}">${xmls.join('')}</dataValidations>`
    : '';
}

// ---------------------------------------------------------------------------
// Shared strings
// ---------------------------------------------------------------------------

interface SharedStrings {
  index: Map<string, number>;
  xml: string;
}

function buildSharedStringsTable(allStrings: string[]): SharedStrings {
  const index = new Map<string, number>();
  for (const s of allStrings) {
    if (!index.has(s)) index.set(s, index.size);
  }
  const count = index.size;
  const items: (string | undefined)[] = new Array<string | undefined>(count);
  for (const [s, i] of index) items[i] = s;
  const si = items
    .map((s) => `<si><t xml:space="preserve">${xmlEscape(s ?? '')}</t></si>`)
    .join('');
  const xml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    `<sst xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" count="${count}" uniqueCount="${count}">${si}</sst>`;
  return { index, xml };
}

// ---------------------------------------------------------------------------
// Single-sheet writer (legacy ledger-web surface)
// ---------------------------------------------------------------------------

const SINGLE_STYLES =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<fonts count="2">' +
  '<font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/></font>' +
  '</fonts>' +
  '<fills count="2">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '</fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="2">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '</cellXfs>' +
  '</styleSheet>';

function renderCell(
  cell: XlsxCell,
  rowNum: number,
  colIdx: number,
  style: number,
  strIndex: Map<string, number>,
  forceText = false,
): string {
  const ref = `${colLetter(colIdx)}${rowNum}`;
  if (typeof cell === 'number' && !forceText) {
    return `<c r="${ref}" s="${style}"><v>${cell}</v></c>`;
  }
  const text = typeof cell === 'number' ? String(cell) : cell;
  const si = strIndex.get(text) ?? 0;
  return `<c r="${ref}" t="s" s="${style}"><v>${si}</v></c>`;
}

function buildSingleWorksheetXml(
  headers: string[],
  rows: XlsxCell[][],
  strIndex: Map<string, number>,
  validations: ColumnValidation[],
): string {
  const headerCells = headers
    .map((h, col) => renderCell(h, 1, col, 1, strIndex))
    .join('');
  const dataRows = rows
    .map((row, i) => {
      const rowNum = i + 2;
      const cells = row
        .map((cell, col) => renderCell(cell, rowNum, col, 0, strIndex))
        .join('');
      return `<row r="${rowNum}">${cells}</row>`;
    })
    .join('');
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    `<sheetData><row r="1">${headerCells}</row>${dataRows}</sheetData>` +
    buildDataValidationsBlock(validations) +
    '</worksheet>'
  );
}

const SINGLE_CONTENT_TYPES =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
  '<Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>' +
  '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
  '</Types>';

const ROOT_RELS =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
  '</Relationships>';

const SINGLE_WORKBOOK =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
  '<sheets><sheet name="Report" sheetId="1" r:id="rId1"/></sheets>' +
  '</workbook>';

const SINGLE_WORKBOOK_RELS =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
  '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>' +
  '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
  '</Relationships>';

/**
 * Build a single-worksheet .xlsx (`"Report"`) from headers + rows. Header row
 * is bold; string cells use a shared-strings table; number cells are inline.
 * This is the exact surface ledger-web's `export_xlsx` re-exports.
 */
export function build_xlsx(
  headers: string[],
  rows: XlsxCell[][],
  column_validations: ColumnValidation[] = [],
): Uint8Array {
  const allStrings: string[] = [...headers];
  for (const row of rows) {
    for (const cell of row) if (typeof cell === 'string') allStrings.push(cell);
  }
  const { index: strIndex, xml: ssXml } = buildSharedStringsTable(allStrings);
  const wsXml = buildSingleWorksheetXml(
    headers,
    rows,
    strIndex,
    column_validations,
  );
  return buildZip([
    { name: '[Content_Types].xml', data: toUtf8(SINGLE_CONTENT_TYPES) },
    { name: '_rels/.rels', data: toUtf8(ROOT_RELS) },
    { name: 'xl/workbook.xml', data: toUtf8(SINGLE_WORKBOOK) },
    { name: 'xl/_rels/workbook.xml.rels', data: toUtf8(SINGLE_WORKBOOK_RELS) },
    { name: 'xl/sharedStrings.xml', data: toUtf8(ssXml) },
    { name: 'xl/styles.xml', data: toUtf8(SINGLE_STYLES) },
    { name: 'xl/worksheets/sheet1.xml', data: toUtf8(wsXml) },
  ]);
}

// ---------------------------------------------------------------------------
// Multi-sheet writer (migration templates)
// ---------------------------------------------------------------------------

/** One worksheet in a {@link buildWorkbookXlsx} template. */
export interface WorkbookSheetSpec {
  /** Worksheet tab name (e.g. `"Employees"`, `"Lists"`, `"Instructions"`). */
  name: string;
  /** Header row cells (row 1). Pass `[]` for a plain instructions sheet. */
  headers: string[];
  /** Data rows (row 2 onward). */
  rows: XlsxCell[][];
  /** Dropdown / date-picker validations for this sheet's columns. */
  validations?: ColumnValidation[];
  /**
   * 0-based column indexes stored and formatted as Text (`@`), so a national
   * ID / account number keeps leading zeros and never becomes scientific
   * notation. Their data cells are always written as strings.
   */
  textColumns?: number[];
  /** Hidden tab (e.g. the `Lists` sheet backing dropdowns). */
  hidden?: boolean;
}

// Styles: 0 normal, 1 bold header, 2 Text (numFmtId 49 = "@").
const MULTI_STYLES =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<fonts count="2">' +
  '<font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/></font>' +
  '</fonts>' +
  '<fills count="2">' +
  '<fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill>' +
  '</fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="3">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '</cellXfs>' +
  '</styleSheet>';

const TEXT_STYLE_INDEX = 2;

function buildMultiWorksheetXml(
  spec: WorkbookSheetSpec,
  strIndex: Map<string, number>,
): string {
  const textCols = new Set(spec.textColumns ?? []);
  const headerCells = spec.headers
    .map((h, col) => renderCell(h, 1, col, 1, strIndex))
    .join('');
  const dataRows = spec.rows
    .map((row, i) => {
      const rowNum = i + 2;
      const cells = row
        .map((cell, col) => {
          const isText = textCols.has(col);
          const style = isText ? TEXT_STYLE_INDEX : 0;
          return renderCell(cell, rowNum, col, style, strIndex, isText);
        })
        .join('');
      return `<row r="${rowNum}">${cells}</row>`;
    })
    .join('');
  const headerRow =
    spec.headers.length > 0 ? `<row r="1">${headerCells}</row>` : '';
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    `<sheetData>${headerRow}${dataRows}</sheetData>` +
    buildDataValidationsBlock(spec.validations ?? []) +
    '</worksheet>'
  );
}

/** Build a multi-worksheet .xlsx template from an ordered list of sheet specs. */
export function buildWorkbookXlsx(sheets: WorkbookSheetSpec[]): Uint8Array {
  if (sheets.length === 0) {
    throw new Error('buildWorkbookXlsx: at least one sheet is required');
  }

  // Collect every string across every sheet for one shared-strings table.
  const allStrings: string[] = [];
  for (const sheet of sheets) {
    allStrings.push(...sheet.headers);
    const textCols = new Set(sheet.textColumns ?? []);
    for (const row of sheet.rows) {
      row.forEach((cell, col) => {
        if (typeof cell === 'string' || textCols.has(col)) {
          allStrings.push(typeof cell === 'number' ? String(cell) : cell);
        }
      });
    }
  }
  const { index: strIndex, xml: ssXml } = buildSharedStringsTable(allStrings);

  const sheetCount = sheets.length;
  const ssRId = `rId${sheetCount + 1}`;
  const stylesRId = `rId${sheetCount + 2}`;

  const sheetOverrides = sheets
    .map(
      (_s, i) =>
        `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    )
    .join('');
  const contentTypes =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    sheetOverrides +
    '<Override PartName="/xl/sharedStrings.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    '</Types>';

  const sheetTags = sheets
    .map((s, i) => {
      const state = s.hidden ? ' state="hidden"' : '';
      return `<sheet name="${xmlEscape(s.name)}" sheetId="${i + 1}"${state} r:id="rId${i + 1}"/>`;
    })
    .join('');
  const workbookXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<sheets>${sheetTags}</sheets>` +
    '</workbook>';

  const worksheetRels = sheets
    .map(
      (_s, i) =>
        `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
    )
    .join('');
  const workbookRels =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    worksheetRels +
    `<Relationship Id="${ssRId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/sharedStrings" Target="sharedStrings.xml"/>` +
    `<Relationship Id="${stylesRId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    '</Relationships>';

  const files: { name: string; data: Uint8Array }[] = [
    { name: '[Content_Types].xml', data: toUtf8(contentTypes) },
    { name: '_rels/.rels', data: toUtf8(ROOT_RELS) },
    { name: 'xl/workbook.xml', data: toUtf8(workbookXml) },
    { name: 'xl/_rels/workbook.xml.rels', data: toUtf8(workbookRels) },
    { name: 'xl/sharedStrings.xml', data: toUtf8(ssXml) },
    { name: 'xl/styles.xml', data: toUtf8(MULTI_STYLES) },
  ];
  sheets.forEach((spec, i) => {
    files.push({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      data: toUtf8(buildMultiWorksheetXml(spec, strIndex)),
    });
  });
  return buildZip(files);
}
